import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { TrendingDown, Gift, Landmark, Plus, X, Trash2 } from "lucide-react";

const CATEGORIES = [
  { id: "all", label: "All Events" },
  { id: "macro", label: "Macro", icon: Landmark, color: "#38bdf8" },
  { id: "unlock", label: "Unlocks", icon: TrendingDown, color: "#f87171" },
  { id: "airdrop", label: "Airdrops", icon: Gift, color: "#4ade80" },
];

function catMeta(category) {
  return CATEGORIES.find((c) => c.id === category) || CATEGORIES[1];
}

function daysUntil(dateStr) {
  const target = new Date(dateStr + "T00:00:00Z");
  const now = new Date();
  const diff = Math.ceil((target - now) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff < 0) return null;
  return `In ${diff} days`;
}

function groupByDate(events) {
  const groups = {};
  events.forEach((e) => {
    if (!groups[e.event_date]) groups[e.event_date] = [];
    groups[e.event_date].push(e);
  });
  return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
}

function AddEventForm({ onAdded, onClose }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("macro");
  const [eventDate, setEventDate] = useState("");
  const [description, setDescription] = useState("");
  const [asset, setAsset] = useState("");
  const [impact, setImpact] = useState("medium");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !eventDate || !description.trim()) return toast.error("Fill in all required fields");
    setSaving(true);
    try {
      await api.post("/calendar/events", {
        title, category, event_date: eventDate, description,
        asset: asset || null, impact: category === "macro" ? impact : null,
      });
      toast.success("Event added");
      setTitle(""); setEventDate(""); setDescription(""); setAsset("");
      onAdded();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add event");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-[var(--bg-panel)] border border-yellow-400/30 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-bold text-lg text-[var(--text-primary)]">Add Calendar Event</h3>
        <button type="button" onClick={onClose} className="text-[var(--text-tertiary)] hover:text-yellow-400 transition"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Event title"
          className="bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
        <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)}
          className="bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
        <select value={category} onChange={(e) => setCategory(e.target.value)}
          className="bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400">
          <option value="macro">Macro / Economic</option>
          <option value="unlock">Token Unlock</option>
          <option value="airdrop">Airdrop</option>
        </select>
        <input value={asset} onChange={(e) => setAsset(e.target.value)} placeholder="Asset ticker (optional, e.g. BTC)"
          className="bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
        {category === "macro" && (
          <select value={impact} onChange={(e) => setImpact(e.target.value)}
            className="bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400">
            <option value="high">High Impact</option>
            <option value="medium">Medium Impact</option>
            <option value="low">Low Impact</option>
          </select>
        )}
      </div>
      <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description..." rows={2}
        className="w-full mt-3 bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400 resize-none" />
      <button type="submit" disabled={saving}
        className="mt-4 bg-yellow-400 text-black font-bold uppercase tracking-wide px-6 py-2.5 hover:bg-yellow-500 transition disabled:opacity-50 text-sm">
        {saving ? "Adding..." : "Add Event"}
      </button>
    </form>
  );
}

export default function Calendar() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/calendar/events", { params: { category: filter, upcoming_only: true } });
      setEvents(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const removeEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await api.delete("/calendar/events/" + id);
      toast.success("Event deleted");
      load();
    } catch { toast.error("Could not delete event"); }
  };

  const grouped = groupByDate(events);

  return (
    <div className="pt-24 pb-20 max-w-3xl mx-auto px-4 sm:px-6">

      {/* Hero */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center mb-10">
        <div className="lg:col-span-8">
          <h1 className="font-display font-bold text-4xl md:text-5xl text-[var(--text-primary)] tracking-tight">Crypto Calendar</h1>
          <p className="mt-3 text-[var(--text-secondary)]">
            Stay ahead of the market with key macro events, token unlocks, and airdrops.
          </p>
        </div>
        <div className="lg:col-span-4 hidden lg:flex justify-end">
          <img src="/images/calendar-hero.png" alt="Crypto Calendar" className="w-56 h-auto" />
        </div>
      </div>

      <div className="flex items-center justify-center gap-2 mb-6 flex-wrap">
        {CATEGORIES.map((c) => (
          <button key={c.id} onClick={() => setFilter(c.id)}
            className={"flex items-center gap-1.5 px-4 py-2 text-xs font-mono uppercase tracking-widest border transition " +
              (filter === c.id ? "bg-yellow-400 text-black border-yellow-400" : "border-[var(--border-soft)] text-[var(--text-tertiary)] hover:border-yellow-400 hover:text-yellow-400")}>
            {c.icon && <c.icon className="w-3.5 h-3.5" />}
            {c.label}
          </button>
        ))}
      </div>

      {user?.role === "admin" && (
        showAdd ? (
          <AddEventForm onAdded={load} onClose={() => setShowAdd(false)} />
        ) : (
          <button onClick={() => setShowAdd(true)}
            className="w-full mb-6 flex items-center justify-center gap-2 border border-dashed border-[var(--border-soft)] hover:border-yellow-400 text-[var(--text-tertiary)] hover:text-yellow-400 py-3 text-sm font-mono uppercase tracking-widest transition">
            <Plus className="w-4 h-4" /> Add Event
          </button>
        )
      )}

      {loading ? (
        <p className="text-center text-[var(--text-tertiary)] font-mono text-sm">Loading events...</p>
      ) : grouped.length === 0 ? (
        <div className="text-center py-16 border border-[var(--border-soft)] bg-[var(--bg-panel)]">
          <p className="text-[var(--text-tertiary)] font-mono text-sm">No upcoming events in this category yet.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map(([date, dayEvents]) => (
            <div key={date}>
              <div className="flex items-center gap-3 mb-3">
                <div className="text-sm font-bold text-[var(--text-primary)] font-mono">
                  {new Date(date + "T00:00:00Z").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
                </div>
                <div className="h-px flex-1 bg-[var(--border-soft)]" />
                {daysUntil(date) && (
                  <span className="text-xs font-mono text-yellow-400 flex-shrink-0">{daysUntil(date)}</span>
                )}
              </div>
              <div className="space-y-2">
                {dayEvents.map((ev) => {
                  const meta = catMeta(ev.category);
                  const Icon = meta.icon;
                  return (
                    <div key={ev.id} className="flex items-start gap-3 bg-[var(--bg-panel)] border border-[var(--border-soft)] p-4"
                      style={{ borderLeftWidth: "3px", borderLeftColor: meta.color }}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: meta.color + "20" }}>
                        <Icon className="w-4 h-4" style={{ color: meta.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-[var(--text-primary)]">{ev.title}</span>
                          {ev.asset && (
                            <span className="text-xs font-mono px-1.5 py-0.5 border border-[var(--border-soft)] text-[var(--text-tertiary)]">{ev.asset}</span>
                          )}
                          {ev.impact && (
                            <span className={"text-xs font-mono px-1.5 py-0.5 border " +
                              (ev.impact === "high" ? "text-red-400 border-red-400/30 bg-red-400/10" :
                               ev.impact === "medium" ? "text-yellow-400 border-yellow-400/30 bg-yellow-400/10" :
                               "text-[var(--text-tertiary)] border-[var(--border-soft)]")}>
                              {ev.impact} impact
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[var(--text-secondary)] mt-1">{ev.description}</p>
                      </div>
                      {user?.role === "admin" && (
                        <button onClick={() => removeEvent(ev.id)} className="text-[var(--text-tertiary)] hover:text-red-400 transition flex-shrink-0">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-16 border-t border-[var(--border-soft)] pt-10">
        <h2 className="font-display font-bold text-2xl text-[var(--text-primary)] mb-6">How to use this calendar</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <Landmark className="w-5 h-5 text-[#38bdf8] mb-2" />
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">Macro Events</div>
            <p className="text-xs text-[var(--text-secondary)]">Fed meetings, CPI releases, and other economic events that historically move crypto markets alongside traditional assets.</p>
          </div>
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <TrendingDown className="w-5 h-5 text-[#f87171] mb-2" />
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">Token Unlocks</div>
            <p className="text-xs text-[var(--text-secondary)]">Dates when previously locked tokens enter circulation, often increasing sell pressure on that asset.</p>
          </div>
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <Gift className="w-5 h-5 text-[#4ade80] mb-2" />
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">Airdrops</div>
            <p className="text-xs text-[var(--text-secondary)]">Upcoming token distributions to eligible wallets or users.</p>
          </div>
        </div>
      </div>
    </div>
  );
}